import React, { useEffect, useState, useCallback } from "react";
import {
  Form,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import "react-toastify/dist/ReactToastify.css";
import "./bankTobank.css";
import bankService from "../../../Services/apiService/bank-service";
import { useAuth } from "../../../Utils/Auth";
import adminService from "../../../Services/apiService/admin-service";
import { toast } from "react-toastify";
import { customErrorHandler, toTitleCase } from "../../../Utils/helper";
import Loader from "../../../Component/loader/index";

const BankToBankTransfer = () => {
  const auth = useAuth();

  const initialFormState = 
    {
    fromBank: "",
    fromBankId : "" ,
    toBank: "",
    toBankId : "" ,
    amount: "",
    admin: "",
    adminId : "",
    remarks: "",
  }

  const initialSerachedState = {
    fromBankQuery: "",
    toBankQuery: "",
    adminQuery: "",
    fromBankResults: [],
    toBankResults: [],
    adminResults: [],
    showFromBankDropdown: false,
    showToBankDropdown: false,
    showAdminDropdown: false,
  }
  
  const [formData, setFormData] = useState(initialFormState);
  const [searchState, setSearchState] = useState(initialSerachedState);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    fromBank: "",
    toBank: "",
    amount: "",
    admin: "",
    remarks : ""
  });

  // Debounce function
  const debounce = (func, delay) => {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // Validate form function
  const validateForm = () => {
    const newErrors = {
      fromBank: "",
      toBank: "",
      amount: "",
      admin: "",
      remarks : ""
    };
    let isValid = true;

    if (!formData.fromBankId) {
      newErrors.fromBank = "Please select a from bank";
      isValid = false;
    }
    if (!formData.toBankId) {
      newErrors.toBank = "Please select a destination bank";
      isValid = false;
    }
    if (formData.fromBankId && formData.toBankId && formData.fromBankId === formData.toBankId) {
      newErrors.toBank = "Source and destination banks must be different";
      isValid = false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
      isValid = false;
    }
    if (!formData.adminId) {
      newErrors.admin = "Please select an admin";
      isValid = false;
    }
    if (!formData.remarks) {
      newErrors.remarks = "Please write a reamrks";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Memoized debounced functions for each search field
  const debouncedFetchBanks = useCallback(
    debounce(async (search, field) => {
      try {
        if (search) {
          const res = await bankService.getActiveBank(auth.user, search);
          if (res) {
            setSearchState(prev => ({
              ...prev,
              [`${field}Results`]: res.data.data,
            }));
          }
        } else {
          setSearchState(prev => ({
            ...prev,
            [`${field}Results`]: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    }, 500),
    [auth.user]
  );

  const debouncedFetchAdmin = useCallback(
    debounce(async (search) => {
      try {
        if (search) {
          const res = await adminService.searchAdminList(auth.user, search);
          if (res) {
            const data = res?.data?.data?.filter((item)=> item?.userName !== auth.user?.userName)
            setSearchState(prev => ({
              ...prev,
              adminResults: data,
            }));
          }
        } else {
          setSearchState(prev => ({
            ...prev,
            adminResults: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    }, 500),
    [auth.user]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;

    setSearchState((prev) => ({
      ...prev,
      [name]: value,
      [`show${
        name.charAt(0).toUpperCase() +
        name.slice(1).replace("Query", "Dropdown")
      }`]: !!value,
    }));

    // Call the appropriate debounced function based on the field
    if (name === 'fromBankQuery' || name === 'toBankQuery') {
      const field = name.replace('Query', '');
      debouncedFetchBanks(value, field);
    } else if (name === 'adminQuery') {
      debouncedFetchAdmin(value);
    }
  };

  const handleSelect = (field, value, id) => {
    setFormData((prev) => ({ ...prev, [field]: value, [`${field}Id`]: id }));
    setSearchState((prev) => ({
      ...prev,
      [`${field}Query`]: "",
      [`show${
        field.charAt(0).toUpperCase() + field.slice(1) + "Dropdown"
      }`]: false,
      [`${field}Results`]: [],
    }));
    // Clear error when user selects an option
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleDropdownVisibility = (field, isVisible) => {
    setSearchState((prev) => ({
      ...prev,
      [`show${field}Dropdown`]: isVisible,
    }));
    if(isVisible){
    let normalizedField = field.charAt(0).toLowerCase() + field.slice(1);
    setFormData((state)=>({
      ...state,
      [normalizedField] : "",
      [`${normalizedField}Id`] : ""
    }))
    setSearchState((prev) => ({  // array to be empty
      ...prev,
    fromBankResults: [],
    toBankResults: [],
    adminResults: [],
    }));
  }
  };

  const handleSubmit = async (e) => { 
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const payload = {
     fromBankId : formData.fromBankId ,
     toBankId : formData.toBankId,
     amount : formData.amount,
     subadminId : formData.adminId,
     remarks : formData.remarks
    }
   try {
    setIsLoading(true)
    const res = await bankService.bankTobankTransfer(payload, auth.user);
    if(res?.data?.success){
      toast.success(toTitleCase(res?.data?.message) || "Success")
      handleClearForm();
    }
  } catch (error) {
    console.log("error", error)
    toast.error(customErrorHandler(error) || "Something Went Wrong")
    console.error("Transfer failed:", error);
  }
  setIsLoading(false)
  };

  const renderDropdown = (items, field, fieldToShow, id) => {
    return (
      <ListGroup className="search-dropdown">
        {items.length > 0 ? (
          items.map((item) => (
            <ListGroup.Item
              key={item[id]}
              action
              onClick={() => handleSelect(field, item[fieldToShow], item[id])}
              className="dropdown-item"
            >
              {item[fieldToShow]}
            </ListGroup.Item>
          ))
        ) : (
          <ListGroup.Item className="dropdown-item text-muted">
            No Results Found
          </ListGroup.Item>
        )}
      </ListGroup>
    );
  };

  const handleClearForm = () => {
    setFormData(initialFormState);
    setSearchState(initialSerachedState);
    setErrors({
      fromBank: "",
      toBank: "",
      amount: "",
      admin: "",
    });
  };

  return (
    <div className="simplified-form-container-fluid">
      <div className="simplified-deposit-form">
        <h5 className="form-header">Bank To Bank Transfer</h5>
        <Form onSubmit={handleSubmit}>
          {/* From Bank Search */}
          <Form.Group className="form-group dropdown-container">
            <Form.Label>From Bank</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                name="fromBankQuery"
                placeholder="Search Bank..."
                value={searchState.fromBankQuery || formData.fromBank}
                onChange={handleSearchChange}
                onFocus={() => handleDropdownVisibility("FromBank", true)}
                onBlur={() =>
                  setTimeout(
                    () => handleDropdownVisibility("FromBank", false),
                    200
                  )
                }
                autoComplete="off"
                className="form-control"
                isInvalid={!!errors.fromBank}
              />
            </InputGroup>
            {searchState.showFromBankDropdown &&
              renderDropdown(searchState.fromBankResults, "fromBank", "bankName", "bankId")}
            <span type="invalid" className="text-danger">
              {toTitleCase(errors.fromBank)}
            </span>
          </Form.Group>

          {/* To Bank Search */}
          <Form.Group className="form-group dropdown-container">
            <Form.Label>Destination Bank </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                name="toBankQuery"
                placeholder="Search Bank..."
                value={searchState.toBankQuery || formData.toBank}
                onChange={handleSearchChange}
                onFocus={() => handleDropdownVisibility("ToBank", true)}
                onBlur={() =>
                  setTimeout(
                    () => handleDropdownVisibility("ToBank", false),
                    200
                  )
                }
                autoComplete="off"
                className="form-control"
                isInvalid={!!errors.toBank}
              />
            </InputGroup>
            {searchState.showToBankDropdown &&
              renderDropdown(searchState.toBankResults, "toBank", "bankName", "bankId")}
            <span type="invalid" className="text-danger">
              {toTitleCase(errors.toBank)}
            </span>
          </Form.Group>

          {/* Amount */}
          <Form.Group className="form-group">
            <Form.Label>Amount </Form.Label>
            <InputGroup>
              <InputGroup.Text className="input-group-text">
                Rs.
              </InputGroup.Text>
              <Form.Control
                type="number"
                name="amount"
                placeholder="Enter Amount"
                value={formData.amount}
                onChange={handleInputChange}
                min="0.01"
                step="0.01"
                className="form-control"
                isInvalid={!!errors.amount}
              />
            </InputGroup>
            <span type="invalid" className="text-danger">
              {toTitleCase(errors.amount)}
            </span>
          </Form.Group>

          {/* Admin Search */}
          <Form.Group className="form-group dropdown-container">
            <Form.Label>Admin Name</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                name="adminQuery"
                placeholder="Search Admin..."
                value={searchState.adminQuery || formData.admin}
                onChange={handleSearchChange}
                onFocus={() => handleDropdownVisibility("Admin", true)}
                onBlur={() =>
                  setTimeout(
                    () => handleDropdownVisibility("Admin", false),
                    200
                  )
                }
                autoComplete="off"
                className="form-control"
                isInvalid={!!errors.admin}
              />
            </InputGroup>
            {searchState.showAdminDropdown &&
              renderDropdown(searchState.adminResults, "admin", "userName", "adminId")}
            <span type="invalid" className="text-danger">
              {toTitleCase(errors.admin)}
            </span>
          </Form.Group>

          {/* Remarks */}
          <Form.Group className="form-group">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              type="text"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              className="form-control"
             isInvalid={!!errors.remarks}
            />
          </Form.Group>
           <span type="invalid" className="text-danger">
              {toTitleCase(errors.remarks)}
          </span>

          {/* Submit Button */}
          <div
            className="form-actions"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              Transfer
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClearForm}
              style={{ flex: 1 }}
            >
              Clear
            </button>
          </div>
        </Form>
      </div>
        <Loader
        isLoading={isLoading}
        spinnerType="bar"
        color="#ff5722"
        message="Processing..."
      />
    </div>
  );
};

export default BankToBankTransfer;